import { shapes } from '@joint/plus';
import AddIcon from '@mui/icons-material/Add';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import CodeIcon from '@mui/icons-material/Code';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ScheduleIcon from '@mui/icons-material/Schedule';
import StarIcon from '@mui/icons-material/Star';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import HubIcon from '@mui/icons-material/Hub';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import DomainIcon from '@mui/icons-material/Domain';
import NoAccountsIcon from '@mui/icons-material/NoAccounts';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import WeekendIcon from '@mui/icons-material/Weekend';
import BedtimeIcon from '@mui/icons-material/Bedtime';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ForumIcon from '@mui/icons-material/Forum';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import {
    Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Popover, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import Input from '../Input/Input';
import PlaceholderPicker from '../PropertyPanel/PlaceholderPicker';
import { insertAtCursor } from '../PropertyPanel/insertAtCursor';
import UpstreamFieldsHint from './UpstreamFieldsHint';
import { useBaseInspector } from './useBaseInspector';

/**
 * ConditionInspector — F7 Fase 1 (completo)
 *
 * Multi-regra, AND/OR entre condições, N portas nomeadas dinâmicas + "Senão"
 * como fallback. Cada regra tem 1..N condições e aponta pra uma porta out
 * criada dinamicamente com o nome escolhido pelo usuário.
 *
 * Persistência: attrs/rules/text (JSON stringificado) + attrs/elsePortId/text.
 * Backend dispatcher (ChatbotFlowController → ConditionEvaluator) lê e roteia.
 *
 * Nomenclatura: "placeholder" (nunca "token").
 */

interface Props {
    cell: shapes.app.LLM;
    chatbotFlowId: string;
}

type Cond = {
    id: string;
    left: string;
    op: string;
    right: string;
};

type Rule = {
    id: string;
    portId: string;
    portLabel: string;
    logic: 'AND' | 'OR';
    conditions: Cond[];
};

const cellProps = {
    label: ['attrs', 'label', 'text'],
    rulesJson: ['attrs', 'rules', 'text'],
    elsePortId: ['attrs', 'elsePortId', 'text'],
};

// Operadores expostos na UI — MESMO NAMES do backend ConditionEvaluator.
// `labelKey` resolve via intl.formatMessage; `defaultLabel` é o fallback pt-BR.
const OPERATORS: Array<{ value: string; labelKey: string; defaultLabel: string; group: string }> = [
    { value: 'equals',        labelKey: 'condition.op.equals',        defaultLabel: 'igual a',                 group: 'text' },
    { value: 'not_equals',    labelKey: 'condition.op.not_equals',    defaultLabel: 'diferente de',            group: 'text' },
    { value: 'contains',      labelKey: 'condition.op.contains',      defaultLabel: 'contém',                  group: 'text' },
    { value: 'not_contains',  labelKey: 'condition.op.not_contains',  defaultLabel: 'não contém',              group: 'text' },
    { value: 'starts_with',   labelKey: 'condition.op.starts_with',   defaultLabel: 'começa com',              group: 'text' },
    { value: 'ends_with',     labelKey: 'condition.op.ends_with',     defaultLabel: 'termina com',             group: 'text' },
    { value: 'is_empty',      labelKey: 'condition.op.is_empty',      defaultLabel: 'é vazio',                 group: 'text' },
    { value: 'is_not_empty',  labelKey: 'condition.op.is_not_empty',  defaultLabel: 'não é vazio',             group: 'text' },
    { value: 'regex_match',   labelKey: 'condition.op.regex_match',   defaultLabel: 'casa regex',              group: 'text' },
    { value: 'gt',            labelKey: 'condition.op.gt',            defaultLabel: 'maior que',               group: 'number' },
    { value: 'gte',           labelKey: 'condition.op.gte',           defaultLabel: 'maior ou igual a',        group: 'number' },
    { value: 'lt',            labelKey: 'condition.op.lt',            defaultLabel: 'menor que',               group: 'number' },
    { value: 'lte',           labelKey: 'condition.op.lte',           defaultLabel: 'menor ou igual a',        group: 'number' },
    { value: 'between',       labelKey: 'condition.op.between',       defaultLabel: 'entre (min,max)',         group: 'number' },
    { value: 'date_equals',   labelKey: 'condition.op.date_equals',   defaultLabel: 'data igual a',            group: 'date' },
    { value: 'date_before',   labelKey: 'condition.op.date_before',   defaultLabel: 'data antes de',           group: 'date' },
    { value: 'date_after',    labelKey: 'condition.op.date_after',    defaultLabel: 'data depois de',          group: 'date' },
    { value: 'date_between',  labelKey: 'condition.op.date_between',  defaultLabel: 'data entre (min,max)',    group: 'date' },
    { value: 'in_last_days',  labelKey: 'condition.op.in_last_days',  defaultLabel: 'nos últimos N dias',      group: 'date' },
    { value: 'is_true',       labelKey: 'condition.op.is_true',       defaultLabel: 'é verdadeiro',            group: 'bool' },
    { value: 'is_false',      labelKey: 'condition.op.is_false',      defaultLabel: 'é falso',                 group: 'bool' },
    { value: 'in',            labelKey: 'condition.op.in',            defaultLabel: 'é um dos valores',        group: 'list' },
    { value: 'not_in',        labelKey: 'condition.op.not_in',        defaultLabel: 'não é nenhum dos valores', group: 'list' },
];

// Operadores unários (não precisam de valor direito).
const UNARY_OPS = new Set(['is_empty', 'is_not_empty', 'is_true', 'is_false']);

// Operadores "negativos" — quando o placeholder resolve pra "" (vazio), esses
// disparam TRUE. Comportamento matematicamente correto, mas admin distraído
// pode se surpreender. Mostramos hint discreto abaixo do select.
const NEGATIVE_OPS = new Set(['not_equals', 'not_contains', 'is_empty', 'is_false', 'not_in']);

// Exemplo real de uso pra cada operador — mostrado no popover do ícone (i).
// Formato: { left, right (vazio pra unários), context — descreve o cenário }.
// Se mudou um operador aqui, atualizar o backend EvaluateOperator também.
const OPERATOR_HINTS: Record<string, { left: string; right: string; context: string }> = {
    equals:        { left: '{{chat.customer.plan}}',       right: 'gold',                          context: 'cliente do plano gold' },
    not_equals:    { left: '{{system.dayofweek}}',         right: 'sat',                           context: 'não é sábado' },
    contains:      { left: '{{chat.customer.email}}',      right: '@empresa.com.br',               context: 'e-mail corporativo' },
    not_contains:  { left: '{{chat.customer.email}}',      right: '@gmail.',                       context: 'evita e-mails pessoais' },
    starts_with:   { left: '{{chat.customer.phone}}',      right: '+55',                           context: 'cliente brasileiro' },
    ends_with:     { left: '{{chat.customer.email}}',      right: '.gov.br',                       context: 'órgão público' },
    is_empty:      { left: '{{chat.customer.name}}',       right: '',                              context: 'cliente anônimo — pede o nome' },
    is_not_empty:  { left: '{{chat.customer.tags}}',       right: '',                              context: 'cliente já tem histórico' },
    regex_match:   { left: '{{chat.customer.phone}}',      right: '^\\+55\\d{10,11}$',             context: 'telefone brasileiro válido' },
    gt:            { left: '{{chat.elapsed_seconds}}',     right: '300',                           context: 'cliente esperando mais de 5 minutos' },
    greater_than:  { left: '{{chat.elapsed_seconds}}',     right: '300',                           context: 'cliente esperando mais de 5 minutos' },
    gte:           { left: '{{system.hour}}',              right: '8',                             context: 'a partir das 8h da manhã' },
    lt:            { left: '{{chat.turn_count}}',          right: '3',                             context: 'primeiras 3 mensagens da conversa' },
    less_than:     { left: '{{chat.turn_count}}',          right: '3',                             context: 'primeiras 3 mensagens da conversa' },
    lte:           { left: '{{chat.transferred_count}}',   right: '1',                             context: 'no máximo 1 transferência' },
    between:       { left: '{{system.hour}}',              right: '8,18',                          context: 'horário comercial (8h às 18h)' },
    date_equals:   { left: '{{system.date}}',              right: '25/12/2026',                    context: 'é dia 25/12/2026 (Natal)' },
    date_before:   { left: '{{system.now}}',               right: '31/12/2026 23:59',              context: 'antes do fim do ano de 2026' },
    date_after:    { left: '{{system.now}}',               right: '01/01/2027 00:00',              context: 'depois do dia 1° de janeiro de 2027' },
    date_between:  { left: '{{system.date}}',              right: '24/12/2026,26/12/2026',         context: 'entre véspera de Natal e dia seguinte' },
    in_last_days:  { left: '$dataUltimaCompra$',           right: '30',                            context: 'comprou nos últimos 30 dias' },
    is_true:       { left: '{{chat.customer.is_new}}',     right: '',                              context: 'cliente novo (primeiro contato)' },
    is_false:      { left: '{{session.user.is_online}}',   right: '',                              context: 'atendente atribuído está offline' },
    in:            { left: '{{system.dayofweek}}',         right: 'sat,sun',                       context: 'é sábado ou domingo (fim de semana)' },
    not_in:        { left: '{{chat.provider.name}}',       right: 'webchat,api',                   context: 'veio de canal diferente de webchat/api' },
};

// ID gerador — evita depender de crypto pra funcionar em ambientes de teste.
const genId = (prefix: string) => `${prefix}-${Math.random().toString(36).slice(2, 8)}`;

// Mapa icon do template (backend devolve string, front resolve MUI icon).
const TEMPLATE_ICONS: Record<string, any> = {
    'person_add':            PersonAddIcon,
    'schedule':              ScheduleIcon,
    'star':                  StarIcon,
    'hourglass_bottom':      HourglassBottomIcon,
    'hub':                   HubIcon,
    'workspace_premium':     WorkspacePremiumIcon,
    'mark_email_unread':     MarkEmailUnreadIcon,
    'domain':                DomainIcon,
    'no_accounts':           NoAccountsIcon,
    'nights_stay':           NightsStayIcon,
    'weekend':               WeekendIcon,
    'bedtime':               BedtimeIcon,
    'swap_horiz':            SwapHorizIcon,
    'forum':                 ForumIcon,
    'admin_panel_settings':  AdminPanelSettingsIcon,
};

const ConditionInspector = (props: Props): ReactElement => {
    const { cell } = props;
    const intl = useIntl();

    const [label, setLabel] = useState<string>('');
    const [rules, setRules] = useState<Rule[]>([]);
    const [elsePortId, setElsePortId] = useState<string>('');

    // F7 Fase 1 fix — BLOQUEIA o listener 'change' do cell (useBaseInspector)
    // durante meus próprios writes. Sem isso: cada changeCellProp dispara
    // cell.change → assignFormFields → lê cell em estado intermediário → reseta
    // state → useEffect default sobrescreve tudo → digitação some.
    const isSyncingRef = useRef(false);
    // Flag pra garantir que o effect de "criar rule default" só roda UMA VEZ
    // por montagem — não pode rodar todo tempo que rules.length===0 (loop).
    const initializedRef = useRef(false);

    // Picker de placeholder — mesmo popover do ServicePropertiesPanel.
    const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
    const activeInputRef = useRef<HTMLInputElement | null>(null);

    // F7 Fase 1 — hint popover do operador (ícone (i)).
    // Guardamos {anchor, opValue} pra saber qual operador mostrar quando o
    // popover abre — o próprio operador que estava na row do clique.
    const [hintPopover, setHintPopover] = useState<{ anchor: HTMLElement; opValue: string } | null>(null);

    // F7 Fase 1 (Opção B #1) — templates prontos.
    const [templatesOpen, setTemplatesOpen] = useState(false);
    const [templates, setTemplates] = useState<any[]>([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);


    // Load: reconstrói state do attrs/rules/text.
    // F7 Fase 1 fix: DEFENSIVO — só sobrescreve se conseguir ler algo válido.
    // Ignora chamadas durante nossos próprios writes (isSyncingRef).
    const assignFormFields = useCallback((): void => {
        if (isSyncingRef.current) return; // não interfere no meio de nossos writes

        setLabel(cell.prop(cellProps.label) ?? '');
        const rulesJson = cell.prop(cellProps.rulesJson);
        const storedElseId = cell.prop(cellProps.elsePortId);

        // elsePortId — só atualiza se vier valor real
        if (storedElseId) setElsePortId(String(storedElseId));

        // rules — só atualiza se JSON parse retornar array não-vazio
        if (!rulesJson) return;
        try {
            const parsed: any[] = JSON.parse(rulesJson);
            if (!Array.isArray(parsed) || parsed.length === 0) return;
            setRules(parsed.map((r: any, i: number) => ({
                id: String(r.id ?? genId('r')),
                portId: String(r.portId ?? ''),
                portLabel: String(r.portLabel ?? `Regra ${String(i + 1).padStart(2, '0')}`),
                logic: (r.logic === 'OR' ? 'OR' : 'AND'),
                conditions: Array.isArray(r.conditions) && r.conditions.length > 0
                    ? r.conditions.map((c: any) => ({
                        id: String(c.id ?? genId('c')),
                        left: String(c.left ?? ''),
                        op: String(c.op ?? 'equals'),
                        right: String(c.right ?? ''),
                    }))
                    : [{ id: genId('c'), left: '', op: 'equals', right: '' }],
            })));
        } catch {
            // JSON inválido — mantém state atual (não sobrescreve).
        }
    }, [cell]);

    const changeCellProp = useBaseInspector({ cell, assignFormFields });

    // Persiste rules + garante portas sincronizadas com o cell.
    // F7 Fase 1 fix: ATOMICO — bloqueia o listener 'change' via isSyncingRef
    // ao longo de TODAS as escritas, evitando que assignFormFields leia
    // estados intermediários e resete o state em memória.
    //
    // ORDEM DAS PORTAS (crítico pra UX): [Regra 1, Regra 2, ..., Regra N, Senão].
    // Senão SEMPRE a última porta out do cell — visualmente aparece no fim, deixa
    // claro pro admin que é fallback (if/elseif/else). Como addPort do Joint
    // empurra pro final do array, fazemos rebuild completo (remove todas + adiciona
    // na ordem certa) toda vez que a topologia de rules muda — barato (poucas portas)
    // e garantido.
    const syncPortsAndPersist = useCallback((next: Rule[], nextElse: string) => {
        isSyncingRef.current = true;
        try {
            // 1) Ordem canônica desejada: rules na ordem + Senão sempre por último.
            const desired: Array<{ id: string; label: string }> = [
                ...next.map(r => ({ id: r.portId, label: r.portLabel || 'Regra' })),
                { id: nextElse, label: 'Senão' },
            ].filter(p => !!p.id);

            // 2) Sync das portas out em 3 passos, sempre PRESERVANDO os links que o
            // admin já conectou (removePorts derruba os links; addPort com mesmo id
            // não reata). Passos:
            //   a) remove só as verdadeiramente órfãs (não estão em desired)
            //   b) adiciona as ausentes (com label)
            //   c) reordena via `prop('ports/items', ...)` — mantém as portas
            //      existentes intactas (com seus links), só troca a ORDEM interna
            //      pra Senão ficar sempre por último.
            const currentOut: any[] = (cell as any).getGroupPorts('out') ?? [];
            const desiredIds = new Set(desired.map(p => p.id));

            // a) órfãs
            const toRemove = currentOut.filter(p => !desiredIds.has(p.id));
            if (toRemove.length > 0) (cell as any).removePorts(toRemove);

            // b) ausentes + update de label das existentes
            const afterRemove: any[] = (cell as any).getGroupPorts('out') ?? [];
            for (const port of desired) {
                const existing = afterRemove.find(p => p.id === port.id);
                if (!existing) {
                    (cell as any).addPort({
                        id: port.id,
                        group: 'out',
                        attrs: { portLabel: { text: port.label } },
                    });
                } else {
                    (cell as any).portProp(port.id, ['attrs', 'portLabel', 'text'], port.label);
                }
            }

            // c) reordena — só se necessário. Reordenar preserva metadata das portas
            // (incluindo o mapeamento pros links já conectados). Usa prop('ports/items',...)
            // pra rewritar o array na ordem canônica: [in ports, ...rules, Senão].
            const finalOut: any[] = (cell as any).getGroupPorts('out') ?? [];
            const currentOrder = finalOut.map(p => p.id).join('|');
            const desiredOrder = desired.map(p => p.id).join('|');
            if (currentOrder !== desiredOrder) {
                const allPorts: any[] = (cell as any).getPorts() ?? [];
                const inPorts = allPorts.filter((p: any) => p.group === 'in');
                const outByDesired = desired
                    .map(d => finalOut.find(p => p.id === d.id))
                    .filter(Boolean);
                (cell as any).prop('ports/items', [...inPorts, ...outByDesired], { rewrite: true });
            }

            // 3) Salva no attrs — rules primeiro, else depois.
            changeCellProp(cellProps.rulesJson, JSON.stringify(next));
            changeCellProp(cellProps.elsePortId, nextElse);
        } finally {
            queueMicrotask(() => { isSyncingRef.current = false; });
        }
    }, [cell, changeCellProp]);

    // Mount: primeira vez que a caixa é aberta SEM rules gravadas, cria 1 default.
    // F7 Fase 1 fix: initializedRef trava — sem isso, esse effect roda toda vez
    // que rules.length===0 (ex.: durante um write intermediário), criando defaults
    // que sobrescrevem a digitação do usuário.
    useEffect(() => {
        if (initializedRef.current) return;
        // Espera 1 tick pra o assignFormFields inicial rodar primeiro (do useBaseInspector).
        const t = setTimeout(() => {
            if (initializedRef.current) return;
            initializedRef.current = true;
            // Se depois do primeiro load NÃO tem rules, cria o default.
            const currentRules = cell.prop(cellProps.rulesJson);
            if (!currentRules) {
                const defaultRule: Rule = {
                    id: genId('r'),
                    portId: genId('port'),
                    portLabel: 'Regra 01',
                    logic: 'AND',
                    conditions: [{ id: genId('c'), left: '', op: 'equals', right: '' }],
                };
                const newElseId = genId('port');
                setRules([defaultRule]);
                setElsePortId(newElseId);
                syncPortsAndPersist([defaultRule], newElseId);
            }
        }, 50);
        return () => clearTimeout(t);
    }, [cell, syncPortsAndPersist]);

    // Handlers CRUD.
    const updateRule = (idx: number, patch: Partial<Rule>) => {
        const next = rules.map((r, i) => i === idx ? { ...r, ...patch } : r);
        setRules(next);
        syncPortsAndPersist(next, elsePortId);
    };

    const updateCond = (rIdx: number, cIdx: number, patch: Partial<Cond>) => {
        const next = rules.map((r, i) => {
            if (i !== rIdx) return r;
            return { ...r, conditions: r.conditions.map((c, j) => j === cIdx ? { ...c, ...patch } : c) };
        });
        setRules(next);
        syncPortsAndPersist(next, elsePortId);
    };

    const addRule = () => {
        // Padronização: "Regra 01", "Regra 02", … com zero-padding pra ordenar/ler bem.
        const nextNum = String(rules.length + 1).padStart(2, '0');
        const rule: Rule = {
            id: genId('r'),
            portId: genId('port'),
            portLabel: `Regra ${nextNum}`,
            logic: 'AND',
            conditions: [{ id: genId('c'), left: '', op: 'equals', right: '' }],
        };
        const next = [...rules, rule];
        setRules(next);
        syncPortsAndPersist(next, elsePortId);
    };

    const removeRule = (idx: number) => {
        if (rules.length <= 1) return;
        const next = rules.filter((_, i) => i !== idx);
        setRules(next);
        syncPortsAndPersist(next, elsePortId);
    };

    const addCondition = (rIdx: number) => {
        const next = rules.map((r, i) => i === rIdx
            ? { ...r, conditions: [...r.conditions, { id: genId('c'), left: '', op: 'equals', right: '' }] }
            : r);
        setRules(next);
        syncPortsAndPersist(next, elsePortId);
    };

    const removeCondition = (rIdx: number, cIdx: number) => {
        const rule = rules[rIdx];
        if (!rule || rule.conditions.length <= 1) return;
        const next = rules.map((r, i) => i === rIdx
            ? { ...r, conditions: r.conditions.filter((_, j) => j !== cIdx) }
            : r);
        setRules(next);
        syncPortsAndPersist(next, elsePortId);
    };

    // Placeholder picker helpers.
    const openPicker = (anchor: HTMLElement, input: HTMLInputElement | null) => {
        activeInputRef.current = input;
        setPickerAnchor(anchor);
    };

    // Templates — fetch on-open. Cachea entre aberturas do dialog.
    const openTemplatesDialog = async () => {
        setTemplatesOpen(true);
        if (templates.length > 0) return;
        setTemplatesLoading(true);
        try {
            const mod: any = await import('src/services/2talk/chatbot-flow.service');
            const res: any = await mod.getConditionTemplates?.();
            const list = res?.data ?? res ?? [];
            setTemplates(Array.isArray(list) ? list : []);
        } catch {
            setTemplates([]);
        } finally {
            setTemplatesLoading(false);
        }
    };

    // Aplica um template — REPLACES rules atuais + regenera portas do zero.
    // Comportamento intencional: template é reset, não merge (evita mistura confusa).
    const applyTemplate = (tpl: any) => {
        const nextRules: Rule[] = (tpl?.rules ?? []).map((r: any) => ({
            id: genId('r'),
            portId: genId('port'),
            portLabel: String(r.portLabel ?? 'Regra'),
            logic: (r.logic === 'OR' ? 'OR' : 'AND'),
            conditions: (r.conditions ?? []).map((c: any) => ({
                id: genId('c'),
                left: String(c.left ?? ''),
                op: String(c.op ?? 'equals'),
                right: String(c.right ?? ''),
            })),
        }));
        // elsePortId novo pra evitar colisão com port removido do template antigo.
        const nextElseId = genId('port');
        setRules(nextRules);
        setElsePortId(nextElseId);
        syncPortsAndPersist(nextRules, nextElseId);
        setTemplatesOpen(false);
    };

    // Validação — cada regra precisa ter portLabel + 1+ condição válida.
    const validationErrors = useMemo(() => {
        const errs: string[] = [];
        rules.forEach((r, i) => {
            if (!r.portLabel?.trim()) errs.push(`Regra #${i + 1} sem nome de porta.`);
            r.conditions.forEach((c, j) => {
                if (!c.left?.trim()) errs.push(`Regra #${i + 1} · condição #${j + 1}: valor de referência vazio.`);
                if (!UNARY_OPS.has(c.op) && !c.right?.trim()) errs.push(`Regra #${i + 1} · condição #${j + 1}: valor esperado vazio.`);
            });
        });
        return errs;
    }, [rules]);

    return (
        <>

            <label>Label
                <Input type="text" value={label} disabled />
            </label>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2, mb: 1 }}>
                <CallSplitIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                    Regras
                </Typography>
                <Chip label={`${rules.length}`} size="small" sx={{ height: 18, fontSize: 10 }} />
                <Tooltip title="Popular com um template pronto">
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AutoAwesomeIcon fontSize="small" />}
                        onClick={openTemplatesDialog}
                        sx={{ fontSize: 10, py: 0.25 }}
                    >
                        Template
                    </Button>
                </Tooltip>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Regras avaliadas em ordem. Primeira que casar dispara a porta correspondente. Se nenhuma casar → porta <strong>Senão</strong>.
            </Typography>

            {rules.map((rule, rIdx) => (
                <Box
                    key={rule.id}
                    sx={{
                        mb: 1.5, p: 1.25, border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1,
                        background: '#fafafa',
                    }}
                >
                    {/* Linha 1: Nome da porta + Logic + Delete */}
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <TextField
                            label="Nome da porta"
                            size="small"
                            value={rule.portLabel}
                            onChange={(e) => updateRule(rIdx, { portLabel: e.target.value })}
                            sx={{ flex: 1 }}
                        />
                        <ToggleButtonGroup
                            size="small"
                            value={rule.logic}
                            exclusive
                            onChange={(_, v) => v && updateRule(rIdx, { logic: v })}
                        >
                            <ToggleButton value="AND" sx={{ fontSize: 10, px: 1 }}>AND</ToggleButton>
                            <ToggleButton value="OR" sx={{ fontSize: 10, px: 1 }}>OR</ToggleButton>
                        </ToggleButtonGroup>
                        <Tooltip title={rules.length <= 1 ? 'Precisa ao menos 1 regra' : 'Remover regra'}>
                            <span>
                                <IconButton size="small" onClick={() => removeRule(rIdx)} disabled={rules.length <= 1}>
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>

                    {/* Condições da regra */}
                    {rule.conditions.map((cond, cIdx) => {
                        const isUnary = UNARY_OPS.has(cond.op);
                        return (
                            <Box
                                key={cond.id}
                                sx={{
                                    mb: 0.75, p: 1, border: '1px dashed rgba(0,0,0,0.15)', borderRadius: 1, background: '#fff',
                                }}
                            >
                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                                        Condição {cIdx + 1}
                                    </Typography>
                                    <Tooltip title={rule.conditions.length <= 1 ? 'Regra precisa ter ao menos 1 condição' : 'Remover condição'}>
                                        <span>
                                            <IconButton size="small" onClick={() => removeCondition(rIdx, cIdx)} disabled={rule.conditions.length <= 1}>
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Stack>

                                {/* LEFT */}
                                <LeftRightInput
                                    label="Valor de referência"
                                    value={cond.left}
                                    onChange={(v) => updateCond(rIdx, cIdx, { left: v })}
                                    onOpenPicker={openPicker}
                                    activeInputRef={activeInputRef}
                                    sx={{ mb: 0.5 }}
                                />
                                {/* 13/10/2026 — Hint de dot-notation quando LEFT referencia variável upstream. */}
                                <UpstreamFieldsHint
                                    cell={cell}
                                    textValue={cond.left}
                                    compact
                                    onInsert={(token) => updateCond(rIdx, cIdx, { left: (cond.left ?? '') + token })}
                                />

                                {/* OPERATOR — Autocomplete pra filtrar por digitação
                                    (Postman/Zendesk-style). Options agrupadas por
                                    group (text/number/date/bool/list). Ícone (i) à
                                    direita abre popover com exemplo real de uso do
                                    operador atualmente selecionado. */}
                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                                    <Autocomplete
                                        size="small"
                                        fullWidth
                                        disableClearable
                                        options={OPERATORS}
                                        groupBy={(o) => {
                                            switch (o.group) {
                                                case 'text':   return 'Texto';
                                                case 'number': return 'Número';
                                                case 'date':   return 'Data';
                                                case 'bool':   return 'Booleano';
                                                case 'list':   return 'Lista';
                                                default:       return o.group;
                                            }
                                        }}
                                        getOptionLabel={(o) =>
                                            intl.formatMessage({ id: o.labelKey, defaultMessage: o.defaultLabel })
                                        }
                                        isOptionEqualToValue={(a, b) => a.value === b.value}
                                        value={OPERATORS.find(o => o.value === cond.op) ?? OPERATORS[0]}
                                        onChange={(_, val) => {
                                            if (val) updateCond(rIdx, cIdx, { op: val.value });
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Operador" size="small" />
                                        )}
                                        sx={{ flex: 1 }}
                                    />
                                    <Tooltip title="Ver exemplo de uso">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => setHintPopover({ anchor: e.currentTarget, opValue: cond.op })}
                                            sx={{ color: '#1976d2', p: 0.5 }}
                                        >
                                            <InfoOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>

                                {/* Hint pra operadores "negativos" — disparam true quando
                                    placeholder resolve pra vazio (matematicamente correto,
                                    mas pega admin distraído de surpresa). */}
                                {NEGATIVE_OPS.has(cond.op) && (
                                    <Alert
                                        severity="info"
                                        icon={<InfoOutlinedIcon fontSize="inherit" />}
                                        sx={{
                                            py: 0, px: 1, mb: 0.75, fontSize: 11,
                                            '& .MuiAlert-message': { py: 0.5 },
                                            '& .MuiAlert-icon': { py: 0.5, mr: 0.75 },
                                        }}
                                    >
                                        Dispara <strong>verdadeiro</strong> quando o valor de referência está vazio.
                                        Para exigir preenchimento, adicione outra condição com <em>não é vazio</em>.
                                    </Alert>
                                )}

                                {/* RIGHT (só se não for operador unário) */}
                                {!isUnary && (
                                    <>
                                        <LeftRightInput
                                            label="Valor esperado"
                                            value={cond.right}
                                            onChange={(v) => updateCond(rIdx, cIdx, { right: v })}
                                            onOpenPicker={openPicker}
                                            activeInputRef={activeInputRef}
                                        />
                                        <UpstreamFieldsHint
                                            cell={cell}
                                            textValue={cond.right}
                                            compact
                                            onInsert={(token) => updateCond(rIdx, cIdx, { right: (cond.right ?? '') + token })}
                                        />
                                    </>
                                )}
                            </Box>
                        );
                    })}

                    <Button
                        size="small" startIcon={<AddIcon />}
                        onClick={() => addCondition(rIdx)}
                        sx={{ fontSize: 11 }}
                    >
                        Adicionar condição
                    </Button>
                </Box>
            ))}

            <Button
                fullWidth size="small" variant="outlined" startIcon={<AddIcon />}
                onClick={addRule}
                sx={{ mb: 2 }}
            >
                Adicionar regra
            </Button>

            {/* Porta Senão — fallback fixo (não editável, não removível) */}
            <Box sx={{ p: 1.25, border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1, background: '#f5f5f5' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label="Senão" size="small" color="default" sx={{ fontWeight: 600 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                        Rota tomada quando nenhuma regra casar. Porta fixa (não pode ser removida).
                    </Typography>
                </Stack>
            </Box>

            {/* Erros de validação (visual — não bloqueia save, só sinaliza) */}
            {validationErrors.length > 0 && (
                <Box sx={{ mt: 1.5, p: 1, border: '1px solid #ffb74d', borderRadius: 1, background: '#fff3e0' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#e65100', display: 'block', mb: 0.5 }}>
                        Atenção — regras incompletas:
                    </Typography>
                    {validationErrors.slice(0, 5).map((e, i) => (
                        <Typography key={i} variant="caption" sx={{ display: 'block', color: '#e65100' }}>
                            • {e}
                        </Typography>
                    ))}
                    {validationErrors.length > 5 && (
                        <Typography variant="caption" sx={{ color: '#e65100' }}>
                            … e mais {validationErrors.length - 5}
                        </Typography>
                    )}
                </Box>
            )}

            {/* Dialog de templates — grid 3 colunas, com Cancelar. CLAUDE.md: div wrapper. */}
            <Dialog
                open={templatesOpen}
                onClose={() => setTemplatesOpen(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AutoAwesomeIcon fontSize="small" color="primary" />
                    Templates de Condição
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        exemplos prontos pra você ver e adaptar
                    </Typography>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    <div style={{ maxHeight: '65vh', overflowY: 'auto', padding: '16px 20px' }}>
                        {templatesLoading && (
                            <Typography variant="body2" color="text.secondary">Carregando templates…</Typography>
                        )}
                        {!templatesLoading && templates.length === 0 && (
                            <Typography variant="body2" color="text.secondary">Nenhum template disponível.</Typography>
                        )}
                        {!templatesLoading && templates.length > 0 && (
                            <>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                                    Clique num template pra <strong>substituir</strong> as regras atuais. Você pode
                                    ajustar depois — os placeholders <code>{'{{chat.*}}'}</code>, <code>{'{{system.*}}'}</code>
                                    e <code>{'{{session.*}}'}</code> resolvem em runtime.
                                </Typography>
                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        sm: '1fr 1fr',
                                        md: 'repeat(3, 1fr)',
                                    },
                                    gap: 1.5,
                                }}>
                                    {templates.map((tpl: any) => {
                                        const Icon = TEMPLATE_ICONS[String(tpl.icon ?? '')] ?? AutoAwesomeIcon;
                                        const ruleCount = (tpl.rules ?? []).length;
                                        const condCount = (tpl.rules ?? []).reduce(
                                            (acc: number, r: any) => acc + (r.conditions ?? []).length, 0);
                                        return (
                                            <Box
                                                key={tpl.id}
                                                sx={{
                                                    p: 1.5,
                                                    border: '1px solid rgba(0,0,0,0.12)',
                                                    borderRadius: 1.5,
                                                    cursor: 'pointer',
                                                    transition: 'all 0.15s',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    height: '100%',
                                                    '&:hover': {
                                                        borderColor: 'primary.main',
                                                        backgroundColor: 'rgba(25,118,210,0.04)',
                                                        transform: 'translateY(-1px)',
                                                        boxShadow: 1,
                                                    },
                                                }}
                                                onClick={() => applyTemplate(tpl)}
                                            >
                                                <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1 }}>
                                                    <Box sx={{
                                                        p: 0.75, borderRadius: 1, background: 'rgba(25,118,210,0.08)',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        flexShrink: 0,
                                                    }}>
                                                        <Icon fontSize="small" color="primary" />
                                                    </Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                                                        {tpl.name}
                                                    </Typography>
                                                </Stack>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, flex: 1 }}>
                                                    {tpl.description}
                                                </Typography>
                                                <Stack direction="row" spacing={0.5}>
                                                    <Chip
                                                        label={`${ruleCount} ${ruleCount === 1 ? 'regra' : 'regras'}`}
                                                        size="small"
                                                        sx={{ height: 18, fontSize: 10 }}
                                                    />
                                                    <Chip
                                                        label={`${condCount} ${condCount === 1 ? 'condição' : 'condições'}`}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ height: 18, fontSize: 10 }}
                                                    />
                                                </Stack>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </>
                        )}
                    </div>
                </DialogContent>
                <DialogActions sx={{ px: 2, py: 1.5 }}>
                    <Button onClick={() => setTemplatesOpen(false)} variant="outlined">
                        Cancelar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Popover — exemplo real de uso do operador selecionado. Aparece
                ao clicar no ícone (i) ao lado do Autocomplete. */}
            <Popover
                open={!!hintPopover}
                anchorEl={hintPopover?.anchor ?? null}
                onClose={() => setHintPopover(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { p: 1.5, maxWidth: 380, borderRadius: 2, boxShadow: 3 } }}
            >
                {(() => {
                    const hint = hintPopover ? OPERATOR_HINTS[hintPopover.opValue] : null;
                    const opMeta = hintPopover ? OPERATORS.find(o => o.value === hintPopover.opValue) : null;
                    if (!hint || !opMeta) return <Typography variant="caption">Sem exemplo cadastrado pra este operador.</Typography>;
                    return (
                        <Box>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                                <InfoOutlinedIcon fontSize="small" sx={{ color: '#1976d2' }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Exemplo de uso
                                </Typography>
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                <strong>{intl.formatMessage({ id: opMeta.labelKey, defaultMessage: opMeta.defaultLabel })}</strong>
                            </Typography>
                            <Box sx={{
                                background: '#1e1e2e', color: '#e0e0e0',
                                fontFamily: "'Fira Code', 'Consolas', monospace",
                                fontSize: 11, lineHeight: 1.5,
                                p: 1, borderRadius: 1, mb: 1,
                                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                            }}>
                                <div><span style={{ color: '#8be9fd' }}>{hint.left}</span></div>
                                <div style={{ color: '#ff79c6' }}>
                                    {intl.formatMessage({ id: opMeta.labelKey, defaultMessage: opMeta.defaultLabel })}
                                </div>
                                {hint.right && (
                                    <div><span style={{ color: '#f1fa8c' }}>{hint.right}</span></div>
                                )}
                            </Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                → {hint.context}
                            </Typography>
                        </Box>
                    );
                })()}
            </Popover>

            <PlaceholderPicker
                open={!!pickerAnchor}
                anchorEl={pickerAnchor}
                onClose={() => setPickerAnchor(null)}
                onInsert={(placeholder) => {
                    insertAtCursor(activeInputRef.current, placeholder);
                    // Dispara mudança sintética pra o React perceber o novo valor no state.
                    if (activeInputRef.current) {
                        const ev = new Event('input', { bubbles: true });
                        activeInputRef.current.dispatchEvent(ev);
                    }
                    setPickerAnchor(null);
                }}
            />
        </>
    );
};

/**
 * Input com botão {} que abre o PlaceholderPicker. Sync bi-direcional via ref
 * pra o cursor position ser respeitado no insert.
 */
const LeftRightInput: React.FC<{
    label: string;
    value: string;
    onChange: (v: string) => void;
    onOpenPicker: (anchor: HTMLElement, input: HTMLInputElement | null) => void;
    activeInputRef: React.MutableRefObject<HTMLInputElement | null>;
    sx?: any;
}> = ({ label, value, onChange, onOpenPicker, activeInputRef, sx }) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    return (
        <TextField
            label={label}
            size="small"
            fullWidth
            value={value}
            onChange={(e) => onChange(e.target.value)}
            inputRef={(el) => { inputRef.current = el; }}
            InputProps={{
                endAdornment: (
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            activeInputRef.current = inputRef.current;
                            onOpenPicker(e.currentTarget as HTMLElement, inputRef.current);
                        }}
                        title="Inserir placeholder"
                        sx={{ p: 0.25 }}
                    >
                        <CodeIcon fontSize="small" />
                    </IconButton>
                ),
            }}
            sx={sx}
        />
    );
};

export default ConditionInspector;
